import { Injectable, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Observable } from "rxjs";
import { ArbetsorderDokumentInfoDTO } from "~app/common/models/planera/v2/ArbetsorderDokumentInfoDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { FeatureService } from "~app/common/services/feature.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "~environments/environment";

export interface IArbetsorderDokument {
    extraInfo?: string;
    typ: string;
    dokumentstatus?: string;
    filnamn: string;
    inaktiverad: boolean;
    skapadTid: string;
    url: string;
}

@Injectable({
    providedIn: "root"
})
export class ArbetsorderDokumentService {
    readonly isAvverkningsdirektivInProgress$: BehaviorSubject<boolean>;
    readonly slutrapportTyp: string = "Slutrapport";

    documents$: BehaviorSubject<IArbetsorderDokument[]>;

    private readonly apiClient = inject(ApiClientService);
    private readonly featureService = inject(FeatureService);

    private readonly stinaSlutrapportFeatureActive = toSignal(this.featureService.getFeatureActive("StinaSlutrapport"));

    constructor() {
        this.documents$ = new BehaviorSubject<IArbetsorderDokument[]>([]);
    }

    createApteringspaket(arbetsorderId: number, skordare: boolean, skotare: boolean): Observable<void> {
        return this.apiClient.post<any, void>(`arbetsorder/${arbetsorderId}/createapteringspaket?recreateSkordare=${skordare}&recreateSkotare=${skotare}`, {});
    }

    createAvverkningsdirektiv(arbetsorderId: number): Observable<void> {
        return this.apiClient.post<any, void>(`arbetsorder/${arbetsorderId}/createavverkningsdirektiv`, {});
    }

    createAvverkningsdirektivUtkast(arbetsorderId: number): Observable<void> {
        return this.apiClient.post<any, void>(`arbetsorder/${arbetsorderId}/createavverkningsdirektiv/utkast`, {});
    }

    fetchUtskrifter(arbetsorderId: number): void {
        this.apiClient
            .get<ArbetsorderDokumentInfoDTO[]>(`arbetsorder/${arbetsorderId}/dokument`)
            .subscribe(result => {
                this.documents$.next(
                    result.map((x): IArbetsorderDokument => {
                        let extraInfo = "";
                        let url = `/core/dokument/arbetsorder/${x.id}`;
                        const inaktiv = !ObjectUtils.isNullOrUndefined(x.inaktiveradDatum) ? true : false;

                        if (!ObjectUtils.isNullOrUndefined(x.maskinnummer)) {
                            extraInfo = x.maskinnummer;
                        }

                        if (!ObjectUtils.isNullOrUndefined(x.dokumentstatus)) {
                            extraInfo = x.dokumentstatus;
                        }

                        if (x.typ === this.slutrapportTyp) {
                            if (this.stinaSlutrapportFeatureActive()) {
                                url = `${environment.slutrapportUrl}/${x.dokumentnyckel}`;
                            } else {
                                url = `${environment.prologGuiBaseUrl}Web/dokument/dsr/${x.dokumentnyckel}`;
                            }
                        }

                        if (inaktiv) {
                            extraInfo = "Inaktiv";
                        }

                        return {
                            filnamn: x.filnamn,
                            skapadTid: DateUtils.formatDateString(x.skapadTid, "YYYY-MM-DD HH:mm:ss"),
                            url: url,
                            extraInfo: extraInfo,
                            inaktiverad: inaktiv,
                            typ: x.typ,
                            dokumentstatus: x.dokumentstatus
                        };
                    })
                );
            });
    }
}
